import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Card, Table, Button, Typography, Space, Alert, Empty, Spin,
  Popconfirm, Dropdown, Modal, DatePicker, TimePicker, Radio, message, Input, Tooltip, Tabs, theme as antdTheme,
} from 'antd';
import {
  PlusOutlined, SendOutlined, DeleteOutlined, MoreOutlined,
  ClockCircleOutlined, MailOutlined, SearchOutlined, EditOutlined,
  CopyOutlined, ExperimentOutlined, BarChartOutlined,
  EyeOutlined, TeamOutlined, RiseOutlined, StopOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import dayjs from 'dayjs';

const { Text } = Typography;

// ── Status badge using CSS variables ────────────────────────────────────────
const STATUS_CONFIG = {
  draft:              { tone: 'neutral',  label: 'Draft' },
  scheduled:          { tone: 'info',     label: 'Scheduled' },
  sending:            { tone: 'warn',     label: 'Sending' },
  completed:          { tone: 'ok',       label: 'Completed' },
  paused_high_bounce: { tone: 'danger',   label: 'Paused — High Bounce' },
  paused_duplicates:  { tone: 'danger',   label: 'Paused — Duplicates' },
};

const TONE_STYLES = {
  neutral: { color: 'var(--text-2)',   background: 'var(--surface-2)', border: '1px solid var(--border)' },
  info:    { color: 'var(--info)',     background: 'var(--info-bg)',   border: '1px solid color-mix(in srgb, var(--info) 30%, transparent)' },
  warn:    { color: 'var(--warn)',     background: 'var(--warn-bg)',   border: '1px solid color-mix(in srgb, var(--warn) 30%, transparent)' },
  ok:      { color: 'var(--ok)',       background: 'var(--ok-bg)',     border: '1px solid color-mix(in srgb, var(--ok) 30%, transparent)' },
  danger:  { color: 'var(--danger)',   background: 'var(--danger-bg)', border: '1px solid color-mix(in srgb, var(--danger) 30%, transparent)' },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  const ts = TONE_STYLES[cfg.tone];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '2px 10px', borderRadius: 999,
      fontSize: 12, fontWeight: 600, lineHeight: '20px',
      whiteSpace: 'nowrap',
      ...ts,
    }}>
      {cfg.label}
    </span>
  );
};

const TAB_FILTERS = ['all', 'draft', 'scheduled', 'sending', 'completed', 'paused_high_bounce'];

const Campaigns = () => {
  const { token: themeToken } = antdTheme.useToken();
  const [campaigns,     setCampaigns]     = useState([]);
  const [total,         setTotal]         = useState(0);
  const [currentPage,   setCurrentPage]   = useState(1);
  const [pageSize]                        = useState(20);
  const [campaignStats, setCampaignStats] = useState({});
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState('');
  const [search,        setSearch]        = useState('');
  const [activeTab,     setActiveTab]     = useState('all');
  const searchDebounceRef = useRef(null);
  const [scheduleModal, setScheduleModal] = useState(null);
  const [scheduleDate,  setScheduleDate]  = useState(null);
  const [scheduleMode,  setScheduleMode]  = useState('fixed'); // 'fixed' | 'recipient_tz'
  const [scheduleLocalDate, setScheduleLocalDate] = useState(null);
  const [scheduleLocalTime, setScheduleLocalTime] = useState(null);
  const [scheduling,    setScheduling]    = useState(false);
  const [testModal,     setTestModal]     = useState(null);
  const [testEmail,     setTestEmail]     = useState('');
  const [testSending,   setTestSending]   = useState(false);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const navigate = useNavigate();

  const fetchCampaigns = async (page = currentPage, searchVal = search) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get('/api/campaigns', {
        headers,
        params: { page, limit: pageSize, search: searchVal },
      });
      const list = res.data.data ?? res.data;
      setCampaigns(list);
      setTotal(res.data.total ?? list.length);
      // Fetch stats for all sent/sending/completed campaigns in parallel
      const sent = list.filter(c => c.status !== 'draft' && c.status !== 'scheduled');
      const results = await Promise.allSettled(
        sent.map(c => axios.get(`/api/campaigns/${c.id}/stats`, { headers }))
      );
      const statsMap = {};
      sent.forEach((c, i) => {
        if (results[i].status === 'fulfilled') statsMap[c.id] = results[i].value.data;
      });
      setCampaignStats(statsMap);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCampaigns(1, search); }, []); // eslint-disable-line

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    setCurrentPage(1);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => fetchCampaigns(1, val), 350);
  };

  // ── Auto-poll stats every 5 s while any campaign is 'sending' ──────────
  const hasSendingCampaigns = campaigns.some(c => c.status === 'sending');
  useEffect(() => {
    if (!hasSendingCampaigns) return;
    const pollId = setInterval(() => fetchCampaigns(currentPage, search), 5000);
    return () => clearInterval(pollId);
  }, [hasSendingCampaigns]); // eslint-disable-line

  const handleSendNow = async (id) => {
    try {
      await axios.post(`/api/campaigns/${id}/send`, { sendNow: true }, { headers });
      message.success('Campaign queued for sending');
      fetchCampaigns(currentPage, search);
    } catch (err) {
      const d = err.response?.data || {};
      if (d.upgrade || d.email_unverified || d.ses_not_connected) return;
      if (d.spam_detected) {
        message.error(d.message || 'Campaign blocked by spam filter', 6);
        return;
      }
      message.error(d.message || 'Failed to send campaign');
    }
  };

  const closeScheduleModal = () => {
    setScheduleModal(null);
    setScheduleDate(null);
    setScheduleMode('fixed');
    setScheduleLocalDate(null);
    setScheduleLocalTime(null);
  };

  const handleSchedule = async () => {
    let payload;
    if (scheduleMode === 'recipient_tz') {
      if (!scheduleLocalDate || !scheduleLocalTime) { message.warning('Please choose a date and a local delivery time'); return; }
      payload = { mode: 'recipient_tz', date: scheduleLocalDate.format('YYYY-MM-DD'), time: scheduleLocalTime.format('HH:mm') };
    } else {
      if (!scheduleDate) { message.warning('Please select a date and time'); return; }
      payload = { scheduleAt: scheduleDate.toISOString() };
    }
    setScheduling(true);
    try {
      const { data } = await axios.post(`/api/campaigns/${scheduleModal}/send`, payload, { headers });
      message.success(data?.message || 'Campaign scheduled');
      closeScheduleModal();
      fetchCampaigns(currentPage, search);
    } catch (err) {
      const d = err.response?.data || {};
      if (d.upgrade || d.email_unverified || d.ses_not_connected) { setScheduling(false); return; }
      message.error(d.message || 'Failed to schedule');
    } finally {
      setScheduling(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/api/campaigns/${id}`, { headers });
      message.success('Campaign deleted');
      const newPage = campaigns.length === 1 && currentPage > 1 ? currentPage - 1 : currentPage;
      setCurrentPage(newPage);
      fetchCampaigns(newPage, search);
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to delete');
    }
  };

  const handleDuplicate = async (id) => {
    try {
      await axios.post(`/api/campaigns/${id}/duplicate`, {}, { headers });
      message.success('Campaign duplicated');
      fetchCampaigns(currentPage, search);
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to duplicate');
    }
  };

  const handleTestSend = async () => {
    if (!testEmail) return;
    setTestSending(true);
    try {
      await axios.post(`/api/campaigns/${testModal}/test`, { email: testEmail }, { headers });
      message.success(`Test email sent to ${testEmail}`);
      setTestModal(null);
      setTestEmail('');
    } catch (err) {
      message.error(err.response?.data?.message || 'Test send failed');
    } finally {
      setTestSending(false);
    }
  };

  // ── Tab counts ──────────────────────────────────────────────────────────
  const tabCounts = TAB_FILTERS.reduce((acc, key) => {
    acc[key] = key === 'all' ? campaigns.length : campaigns.filter(c => c.status === key).length;
    return acc;
  }, {});

  const displayedCampaigns = activeTab === 'all'
    ? campaigns
    : campaigns.filter(c => c.status === activeTab);

  const TAB_LABELS = {
    all:                'All',
    draft:              'Drafts',
    scheduled:          'Scheduled',
    sending:            'Sending',
    completed:          'Completed',
    paused_high_bounce: 'Archived',
    paused_duplicates:  'Archived',
  };

  const tabItems = TAB_FILTERS.map(key => ({
    key,
    label: (
      <span>
        {TAB_LABELS[key]}
        {tabCounts[key] > 0 && (
          <span style={{
            marginLeft: 6, padding: '1px 6px', borderRadius: 999,
            background: activeTab === key ? 'var(--brand-tint)' : 'var(--surface-2)',
            color: activeTab === key ? 'var(--brand-primary)' : 'var(--text-3)',
            fontSize: 11, fontFamily: 'var(--font-mono)', fontWeight: 600,
          }}>
            {tabCounts[key]}
          </span>
        )}
      </span>
    ),
  }));

  const columns = [
    {
      title: 'Campaign', dataIndex: 'name', width: 220, ellipsis: true,
      render: (name, record) => (
        <Space size={10}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: 'var(--brand-tint)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <MailOutlined style={{ color: 'var(--brand-primary)', fontSize: 16 }} />
          </div>
          <div>
            <div style={{ fontWeight: 600, color: 'var(--text)', fontSize: 13 }}>{name}</div>
            <div style={{ color: 'var(--text-3)', fontSize: 12 }}>{record.subject}</div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Status', dataIndex: 'status', width: 160,
      render: (s, record) => {
        const badge = <StatusBadge status={s} />;
        if (s === 'scheduled' && record.scheduled_at) {
          const tz = record.send_mode === 'recipient_tz' ? (record.tz_config || null) : null;
          const tip = tz
            ? `${tz.time} in each recipient's local timezone · ${tz.date}`
            : new Date(record.scheduled_at).toLocaleString();
          return (
            <Tooltip title={<Space size={4}><ClockCircleOutlined />{tip}</Space>}>
              {badge}
            </Tooltip>
          );
        }
        return badge;
      },
    },
    {
      title: 'Created', dataIndex: 'created_at', width: 130, responsive: ['lg'],
      render: d => (
        <span style={{ fontSize: 12, color: 'var(--text-3)' }}>
          {new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        </span>
      ),
    },
    {
      title: 'Performance', key: 'stats', width: 280, responsive: ['sm'],
      render: (_, record) => {
        const s = campaignStats[record.id];
        if (record.status === 'sending') {
          const sentSoFar = s ? (s.total || 0) : 0;
          return (
            <Space size={6}>
              <Spin indicator={<LoadingOutlined style={{ color: 'var(--warn)', fontSize: 13 }} spin />} />
              <span style={{ fontSize: 12, color: 'var(--warn)', fontWeight: 600 }}>
                Sending{sentSoFar > 0 ? ` — ${sentSoFar} sent` : '…'}
              </span>
            </Space>
          );
        }
        if (record.status === 'draft' || record.status === 'scheduled' || !s) {
          return <span style={{ color: 'var(--text-3)', fontSize: 12 }}>—</span>;
        }
        const pills = [
          { icon: <TeamOutlined />,  value: s.total,           label: 'sent',    tone: 'info' },
          { icon: <EyeOutlined />,   value: `${s.openRate}%`,  label: 'opened',  tone: 'ok' },
          { icon: <RiseOutlined />,  value: `${s.clickRate}%`, label: 'clicks',  tone: 'info' },
          { icon: <StopOutlined />,  value: s.bounced,         label: 'bounced', tone: 'danger' },
        ];
        return (
          <Space size={6} wrap>
            {pills.map(p => {
              const ts = TONE_STYLES[p.tone];
              return (
                <div key={p.label} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '2px 8px', borderRadius: 20,
                  background: ts.background, border: ts.border,
                }}>
                  {React.cloneElement(p.icon, { style: { fontSize: 10, color: ts.color } })}
                  <span style={{ fontSize: 11, fontWeight: 600, color: ts.color, fontFamily: 'var(--font-mono)' }}>{p.value}</span>
                  <span style={{ fontSize: 10, color: ts.color, opacity: 0.7 }}>{p.label}</span>
                </div>
              );
            })}
          </Space>
        );
      },
    },
    {
      title: '', key: 'actions', align: 'right', width: 80,
      render: (_, record) => {
        const isDraft = record.status === 'draft';
        const menuItems = [
          ...(isDraft ? [
            { key: 'edit',     label: <Space><EditOutlined />Edit</Space>,                  onClick: () => navigate(`/campaigns/${record.id}/edit`) },
            { key: 'send',     label: <Space><SendOutlined />Send Now</Space>,              onClick: () => handleSendNow(record.id) },
            { key: 'schedule', label: <Space><ClockCircleOutlined />Schedule</Space>,       onClick: () => setScheduleModal(record.id) },
          ] : []),
          { key: 'test',      label: <Space><ExperimentOutlined />Send Test</Space>,        onClick: () => { setTestModal(record.id); setTestEmail(''); } },
          { key: 'duplicate', label: <Space><CopyOutlined />Duplicate</Space>,              onClick: () => handleDuplicate(record.id) },
          { type: 'divider' },
          {
            key: 'delete', danger: true,
            label: (
              <Popconfirm
                title="Delete campaign?"
                onConfirm={() => handleDelete(record.id)}
                okText="Delete"
                okButtonProps={{ danger: true }}
                onClick={e => e.stopPropagation()}
              >
                <Space><DeleteOutlined />Delete</Space>
              </Popconfirm>
            ),
          },
        ];
        return (
          <div onClick={e => e.stopPropagation()}>
            <Space size={4}>
              {!isDraft && (
                <Button
                  icon={<BarChartOutlined />}
                  type="text"
                  size="small"
                  style={{ color: 'var(--brand-primary)' }}
                  title="View Report"
                  onClick={() => navigate(`/campaigns/${record.id}/report`)}
                />
              )}
              <Dropdown menu={{ items: menuItems }} trigger={['click']} placement="bottomRight">
                <Button icon={<MoreOutlined />} type="text" size="small" />
              </Dropdown>
            </Space>
          </div>
        );
      },
    },
  ];

  return (
    <div>
      {/* Header row — no page title (topbar owns it) */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: 20, gap: 12 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          size="large"
          onClick={() => navigate('/campaigns/create')}
          style={{ borderRadius: 'var(--r-md)', fontWeight: 600 }}
        >
          New Campaign
        </Button>
      </div>

      {error && (
        <Alert
          message={error}
          type="error"
          showIcon
          closable
          onClose={() => setError('')}
          style={{ marginBottom: 16, borderRadius: 'var(--r-md)' }}
        />
      )}

      <div style={{
        borderRadius: 'var(--r-lg)',
        border: '1px solid var(--border)',
        boxShadow: 'var(--shadow-sm)',
        background: 'var(--surface)',
        overflow: 'hidden',
      }}>
        {/* Search + Tabs toolbar */}
        <div style={{
          padding: '12px 16px 0',
          borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap',
        }}>
          <Tabs
            activeKey={activeTab}
            onChange={key => { setActiveTab(key); setCurrentPage(1); }}
            items={tabItems}
            size="small"
            style={{ flex: 1, marginBottom: 0 }}
            tabBarStyle={{ marginBottom: 0 }}
          />
          <div style={{ paddingBottom: 8 }}>
            <Input
              placeholder="Search campaigns…"
              prefix={<SearchOutlined style={{ color: 'var(--text-3)' }} />}
              value={search}
              onChange={handleSearchChange}
              onClear={() => { setSearch(''); setCurrentPage(1); fetchCampaigns(1, ''); }}
              style={{ width: 240, borderRadius: 'var(--r-md)' }}
              allowClear
              size="small"
            />
          </div>
        </div>

        <Table
          dataSource={displayedCampaigns}
          columns={columns}
          rowKey="id"
          loading={loading}
          onRow={record => ({
            onClick: () => record.status === 'draft'
              ? navigate(`/campaigns/${record.id}/edit`)
              : navigate(`/campaigns/${record.id}/report`),
            style: { cursor: 'pointer' },
          })}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No campaigns yet. Create your first campaign!" /> }}
          pagination={{
            current: currentPage,
            pageSize,
            total: displayedCampaigns.length,
            showTotal: t => `${t} campaign${t !== 1 ? 's' : ''}`,
            onChange: (page) => { setCurrentPage(page); fetchCampaigns(page, search); },
            showSizeChanger: false,
            style: { padding: '8px 16px' },
          }}
          scroll={{ x: 'max-content' }}
          style={{ borderRadius: 0 }}
        />
      </div>

      {/* Test Send Modal */}
      <Modal
        title={<Space><ExperimentOutlined style={{ color: 'var(--brand-primary)' }} /><span>Send Test Email</span></Space>}
        open={!!testModal}
        onCancel={() => { setTestModal(null); setTestEmail(''); }}
        onOk={handleTestSend}
        okText="Send Test"
        okButtonProps={{ loading: testSending, style: { borderRadius: 8 } }}
        cancelButtonProps={{ style: { borderRadius: 8 } }}
        width={400}
      >
        <div style={{ marginTop: 20 }}>
          <Text style={{ color: 'var(--text-2)', display: 'block', marginBottom: 8 }}>
            Send a preview of this campaign to any email address.
          </Text>
          <Input
            type="email"
            placeholder="test@example.com"
            value={testEmail}
            onChange={e => setTestEmail(e.target.value)}
            onPressEnter={handleTestSend}
            size="large"
            style={{ borderRadius: 'var(--r-md)' }}
          />
        </div>
      </Modal>

      {/* Schedule Modal */}
      <Modal
        title={<Space><ClockCircleOutlined style={{ color: 'var(--brand-primary)' }} /><span>Schedule Campaign</span></Space>}
        open={!!scheduleModal}
        onCancel={closeScheduleModal}
        onOk={handleSchedule}
        okText="Schedule"
        okButtonProps={{ loading: scheduling, style: { borderRadius: 8 } }}
        cancelButtonProps={{ style: { borderRadius: 8 } }}
        width={440}
      >
        <div style={{ marginTop: 20 }}>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>Delivery time</Text>
          <Radio.Group
            value={scheduleMode}
            onChange={e => setScheduleMode(e.target.value)}
            style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 18 }}
          >
            <Radio value="fixed">At a specific time (everyone at once)</Radio>
            <Radio value="recipient_tz">In each recipient's local timezone</Radio>
          </Radio.Group>

          {scheduleMode === 'fixed' ? (
            <>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>Select date and time</Text>
              <DatePicker
                showTime
                value={scheduleDate ? dayjs(scheduleDate) : null}
                style={{ width: '100%', borderRadius: 'var(--r-md)' }}
                size="large"
                disabledDate={d => d && d < dayjs().startOf('day')}
                onChange={val => setScheduleDate(val ? val.toDate() : null)}
              />
            </>
          ) : (
            <>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>Date &amp; local delivery time</Text>
              <Space.Compact style={{ width: '100%' }}>
                <DatePicker
                  value={scheduleLocalDate}
                  style={{ width: '60%', borderRadius: 'var(--r-md)' }}
                  size="large"
                  disabledDate={d => d && d < dayjs().startOf('day')}
                  onChange={setScheduleLocalDate}
                />
                <TimePicker
                  value={scheduleLocalTime}
                  format="HH:mm"
                  minuteStep={5}
                  style={{ width: '40%', borderRadius: 'var(--r-md)' }}
                  size="large"
                  onChange={setScheduleLocalTime}
                />
              </Space.Compact>
              <Text type="secondary" style={{ display: 'block', marginTop: 10, fontSize: 12, lineHeight: 1.5 }}>
                Each recipient receives the email at this wall-clock time in their own timezone (inferred from their country, or an explicit timezone if set). Recipients whose local time has already passed are sent immediately.
              </Text>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default Campaigns;
